import {
  CheckCircle2,
  CodeXml,
  Handshake,
  Lock,
  Smartphone,
  ToggleRight,
  type LucideIcon,
} from "lucide-react";

import { ColocaReveal } from "@/shared/components/motion/ColocaReveal";
import type { CardsTranslations } from "@/shared/lang/interfaces/cards";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { CardBrandingDiagram } from "./CardBrandingDiagram";

/**
 * `enabled` / `locked`: the organization has the switch on or off. `staff`: an internal user, who turns it on
 * from Organizations rather than asking for it.
 */
export type CardBrandingStatus = "enabled" | "locked" | "staff";

type CardsCopyKey = keyof CardsTranslations;

interface Point {
  icon: LucideIcon;
  title: CardsCopyKey;
  body: CardsCopyKey;
}

const POINTS: Point[] = [
  { icon: CodeXml, title: "branding_point_iframe_title", body: "branding_point_iframe_body" },
  { icon: Smartphone, title: "branding_point_wallets_title", body: "branding_point_wallets_body" },
  { icon: Handshake, title: "branding_point_cobrand_title", body: "branding_point_cobrand_body" },
];

const STATUS_BODY: Record<CardBrandingStatus, CardsCopyKey> = {
  enabled: "branding_point_enabled_on",
  locked: "branding_point_enabled_off",
  staff: "branding_point_enabled_staff",
};

const STATUS_ICON: Record<CardBrandingStatus, LucideIcon> = {
  enabled: CheckCircle2,
  locked: Lock,
  staff: ToggleRight,
};

export interface CardBrandingExplainerProps {
  status: CardBrandingStatus;
  className?: string;
}

/**
 * Where the card design applies, said before anyone picks a colour: only the secure card view the API opens
 * (an iframe), never the tokenized card in Apple Pay or Google Pay; always next to the Coloca mark; and only
 * for organizations Coloca turned it on for. The last point reads the organization's own state.
 */
export function CardBrandingExplainer({ status, className }: CardBrandingExplainerProps) {
  const { t } = useTranslations("cards");
  const StatusIcon = STATUS_ICON[status];

  return (
    <section
      aria-labelledby="card-branding-explainer-title"
      className={cn(
        "grid gap-6 rounded-2xl border border-border bg-card p-5 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] lg:items-center lg:p-6",
        className,
      )}
    >
      <div className="mx-auto w-full max-w-lg rounded-xl border border-border bg-muted/40 px-3 py-4 lg:max-w-none">
        <CardBrandingDiagram />
      </div>

      <ColocaReveal className="flex min-w-0 flex-col gap-4" delay={0.4}>
        <div data-reveal>
          <h2 id="card-branding-explainer-title" className="text-base font-semibold text-foreground">
            {t("branding_explainer_title")}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">{t("branding_explainer_lead")}</p>
        </div>
        <ul className="grid gap-4 sm:grid-cols-2">
          {POINTS.map(({ icon: Icon, title, body }) => (
            <li key={title} data-reveal className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Icon aria-hidden="true" className="size-4" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{t(title)}</p>
                <p className="text-sm text-muted-foreground">{t(body)}</p>
              </div>
            </li>
          ))}
          <li data-reveal className="flex items-start gap-3">
            <span
              className={cn(
                "mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-lg",
                status === "locked" ? "bg-muted text-muted-foreground" : "bg-primary/10 text-primary",
              )}
            >
              <StatusIcon aria-hidden="true" className="size-4" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">{t("branding_point_enabled_title")}</p>
              <p className="text-sm text-muted-foreground">{t(STATUS_BODY[status])}</p>
            </div>
          </li>
        </ul>
      </ColocaReveal>
    </section>
  );
}
